import React, { useState } from "react";
import * as Styled from "./styled";
import * as PerStyled from "../styled";
import PropTypes from "prop-types";
import CreateUserModal from "../CreateUserModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  createLocation,
  loadLocations,
} from "../../../store/locations/actions";
import { useDispatch, useSelector } from "react-redux";
import { validation } from "../../../utils";
import { addApiMessage } from "../../../store/messageBar/actions";
import { Map, TextField, Button, Modal } from "@6tamp/uikit";
import { useTranslation } from "react-i18next";
import { useMetrika } from "yandex-metrika-react";

const fieldTypes = Object.freeze({
  Single: Symbol("single"),
  Double: Symbol("double"),
});

const CreateLocationModal = ({ isOpen, close }) => {
  const ym = useMetrika();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector(({ locations }) => locations.isLoading);
  const userId = useSelector(({ auth }) => auth?.id);
  const fields = [
    {
      type: fieldTypes.Single,
      id: "title",
      placeholder: t("views.locations.modal_fields_placeholders.title"),
    },
    {
      type: fieldTypes.Single,
      id: "address",
      placeholder: t("views.locations.modal_fields_placeholders.address"),
    },
    {
      type: fieldTypes.Double,
      left: {
        id: "from",
        placeholder: t("views.locations.modal_fields_placeholders.from"),
      },
      right: {
        id: "to",
        placeholder: t("views.locations.modal_fields_placeholders.to"),
      },
    },
    {
      type: fieldTypes.Single,
      id: "geoPushMessage",
      placeholder: t(
        "views.locations.modal_fields_placeholders.geo_push_message"
      ),
    },
    {
      type: fieldTypes.Double,
      left: {
        id: "latitude",
        placeholder: t("views.locations.modal_fields_placeholders.latitude"),
      },
      right: {
        id: "longitude",
        placeholder: t("views.locations.modal_fields_placeholders.longitude"),
      },
    },
  ];

  const [point, setPoint] = useState([]);

  const formik = useFormik({
    initialValues: {
      title: "",
      address: "",
      from: "",
      to: "",
      geoPushMessage: "",
      latitude: "",
      longitude: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required(t("core.validation.is_required")),
      address: Yup.string().required(t("core.validation.is_required")),
      from: Yup.string(),
      to: Yup.string(),
      geoPushMessage: Yup.string(),
      latitude: Yup.number(t("core.validation.coordinates.invalid_value"))
        .typeError(t("core.validation.coordinates.invalid_type"))
        .min(-90, t("core.validation.coordinates.invalid_value"))
        .max(90, t("core.validation.coordinates.invalid_value")),
      longitude: Yup.number(t("core.validation.coordinates.invalid_value"))
        .typeError(t("core.validation.coordinates.invalid_type"))
        .min(-180, t("core.validation.coordinates.invalid_value"))
        .max(180, t("core.validation.coordinates.invalid_value")),
    }),
    onSubmit: async (values) => {
      const { title, address, from, to, geoPushMessage, latitude, longitude } =
        values;
      const request = {
        title,
        geoPushMessage,
        address,
        position: {
          latitude,
          longitude,
        },
        workingHours: {
          from,
          to,
        },
      };
      const { result, msg } = await dispatch(createLocation(request));
      dispatch(addApiMessage(result ? "success" : "alert", msg));
      if (result) {
        dispatch(loadLocations(null, "active"));
        close();
        formik.resetForm();
      }

      if (request.geoPushMessage) {
        ym("reachGoal", "geoPush", { userId });
      }
    },
  });

  const onClose = () => {
    close();
    formik.resetForm();
  };

  const onMapClick = async ([latitude, longitude]) => {
    setPoint([latitude, longitude]);
    await Promise.all([
      formik.setFieldValue("latitude", latitude.toString()),
      formik.setFieldValue("longitude", longitude.toString()),
    ]);
  };

  return (
    <Modal
      isOpen={isOpen}
      title={t("views.locations.create_location_modal_title")}
      description={t("views.locations.create_location_modal_desc")}
      onClose={onClose}
    >
      <Styled.Form
        onSubmit={formik.handleSubmit}
        onChange={formik.handleChange}
      >
        <PerStyled.LocationInputsContainer>
          {fields.map((field) => {
            if (field.type === fieldTypes.Single) {
              return (
                <TextField
                  key={field.id}
                  id={field.id}
                  name={field.id}
                  placeholder={field.placeholder}
                  value={formik.values[field.id]}
                  width={"full"}
                  height={40}
                  state={validation.isError(formik, field.id) ? "error" : ""}
                  errorText={formik.errors[field.id]}
                />
              );
            } else {
              return (
                <PerStyled.DoubledInputContainer
                  key={`${field.left.id}_${field.right.id}`}
                >
                  <TextField
                    key={field.left.id}
                    id={field.left.id}
                    name={field.left.id}
                    placeholder={field.left.placeholder}
                    value={formik.values[field.left.id]}
                    width={"full"}
                    height={40}
                    state={
                      validation.isError(formik, field.left.id) ? "error" : ""
                    }
                    errorText={formik.errors[field.left.id]}
                  />
                  <TextField
                    key={field.right.id}
                    id={field.right.id}
                    name={field.right.id}
                    placeholder={field.right.placeholder}
                    value={formik.values[field.right.id]}
                    width={"full"}
                    height={40}
                    state={
                      validation.isError(formik, field.right.id) ? "error" : ""
                    }
                    errorText={formik.errors[field.right.id]}
                  />
                </PerStyled.DoubledInputContainer>
              );
            }
          })}
        </PerStyled.LocationInputsContainer>
        <Map onGetCoords={onMapClick} points={[point]} height={"22vh"} />
        <PerStyled.ModalButtonWrapper>
          <Button
            width={"full"}
            round={true}
            type={"submit"}
            text={t("views.locations.btn_create_location")}
            isLoading={isLoading}
          />
        </PerStyled.ModalButtonWrapper>
      </Styled.Form>
    </Modal>
  );
};

CreateUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
};
export default CreateLocationModal;
